import {
  DateFilterModel,
  FilterModel,
  NumberFilterModel,
  TextFilterModel,
} from 'ag-grid-enterprise';
import {
  ListContractHistoryFilter,
  ListContractHistoryFilterSchema,
} from '@/connect/gen/modules/apiengine/services/contracts/contracts_pb';
import {
  ContractHistoryEventInitiatorKind,
  ContractHistoryEventType,
} from '@/connect/gen/consts/loanhistoryconsts_pb';
import {
  BigintFilter,
  BigintFilterSchema,
  DecimalFilter,
  DecimalFilterSchema,
} from '@/connect/gen/consts/queryconsts_pb';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import { SettlementType as PbSettlementType } from '@/connect/gen/consts/settlementconsts_pb';
import { addDays } from 'date-fns';
import { create } from '@/connect';

export function getListFilter(filterModel: FilterModel): ListContractHistoryFilter {
  if (!Object.keys(filterModel).length) {
    return create(ListContractHistoryFilterSchema, {});
  }

  return create(ListContractHistoryFilterSchema, {
    eventTypes: (filterModel.eventType as { values?: ContractHistoryEventType[] })?.values ?? [],
    ...getEventTimeFilter(filterModel.eventTime),
    ticker: (filterModel['contractState.instrument.ticker'] as { filter?: string })?.filter,
    cusip: (filterModel['contractState.instrument.cusip'] as { filter?: string })?.filter,
    counterparties:
      (filterModel['contractState.counterpartyId'] as { values?: string[] })?.values ?? [],
    contracts: getContractsFilter(filterModel['contractState.id']),
    side: (filterModel.side as { filter?: Side | null })?.filter ?? undefined,
    ...getInitiatorFilter(filterModel.initiator),
    openQuantity: toBigintFilter(filterModel['contractState.openQuantity'] as NumberFilterModel),
    contractValue: toDecimalFilter(filterModel['contractState.contractValue'] as NumberFilterModel),
    settlementTypes:
      (filterModel['contractState.settlementType'] as { values?: PbSettlementType[] })?.values ??
      [],
    openQuantityChange: toBigintFilter(
      filterModel['contractState.openQuantityChange'] as NumberFilterModel
    ),
    rate: toDecimalFilter(filterModel['contractState.rateDetails'] as NumberFilterModel),
    rateAmount: toDecimalFilter(
      filterModel['contractState.interestDueAmount'] as NumberFilterModel
    ),
    unitPrice: toDecimalFilter(filterModel['contractState.unitPrice'] as NumberFilterModel),
    contractValueAdjustment: toDecimalFilter(
      filterModel['contractState.contractValueAdjustment'] as NumberFilterModel
    ),
    ...getSettlementDateFilter(filterModel['contractState.settlementDate']),
    marketValue: toDecimalFilter(filterModel['contractState.marketValue'] as NumberFilterModel),
  });
}

export function toDecimalFilter(model: NumberFilterModel | undefined): DecimalFilter | undefined {
  if (model?.filter == null) return undefined;
  return create(DecimalFilterSchema, {
    type: model.type || 'equals',
    filter: String(model.filter),
    filterTo: model.filterTo != null ? String(model.filterTo) : undefined,
  });
}

export function toBigintFilter(model: NumberFilterModel | undefined): BigintFilter | undefined {
  if (model?.filter == null) return undefined;
  return create(BigintFilterSchema, {
    type: model.type || 'equals',
    filter: BigInt(model.filter),
    filterTo: model.filterTo != null ? BigInt(model.filterTo) : undefined,
  });
}

export function getContractsFilter(model: TextFilterModel | undefined): string[] {
  const id = model?.filter;
  return id ? [id] : [];
}

export function getEventTimeFilter(
  model: DateFilterModel | undefined
): Partial<Pick<ListContractHistoryFilter, 'eventTimeStart' | 'eventTimeEnd'>> {
  if (!model?.dateFrom) return {};

  const startOfDay = new Date(`${model.dateFrom.split('T')[0]}T00:00:00.000Z`);
  const startOfDayISO = startOfDay.toISOString();
  const startOfNextDayISO = addDays(startOfDay, 1).toISOString();

  switch (model.type) {
    case 'lessThanOrEqual':
      return { eventTimeEnd: startOfNextDayISO };
    case 'greaterThanOrEqual':
      return { eventTimeStart: startOfDayISO };
    case 'inRange': {
      const endOfDay = model.dateTo
        ? new Date(`${model.dateTo.split('T')[0]}T00:00:00.000Z`)
        : startOfDay;
      const [rangeStart, rangeEnd] =
        endOfDay < startOfDay ? [endOfDay, startOfDay] : [startOfDay, endOfDay];
      return {
        eventTimeStart: rangeStart.toISOString(),
        eventTimeEnd: addDays(rangeEnd, 1).toISOString(),
      };
    }
    case 'equals':
    default:
      return {
        eventTimeStart: startOfDayISO,
        eventTimeEnd: startOfNextDayISO,
      };
  }
}

export function getInitiatorFilter(
  model: TextFilterModel | undefined
): Partial<Pick<ListContractHistoryFilter, 'initiatorKind' | 'initiatorId'>> {
  const value = model?.filter?.trim();
  if (!value) return {};

  const kind =
    ContractHistoryEventInitiatorKind[
      value.toUpperCase() as keyof typeof ContractHistoryEventInitiatorKind
    ];
  return kind !== undefined ? { initiatorKind: kind } : { initiatorId: value };
}

export function getSettlementDateFilter(
  model: DateFilterModel | undefined
): Partial<Pick<ListContractHistoryFilter, 'settlementDateStart' | 'settlementDateEnd'>> {
  if (!model?.dateFrom) return {};

  const settlementDateStart = model.dateFrom.split('T')[0];

  switch (model.type) {
    case 'lessThanOrEqual':
      return { settlementDateEnd: settlementDateStart };
    case 'greaterThanOrEqual':
      return { settlementDateStart };
    case 'inRange': {
      const settlementDateEnd = model.dateTo ? model.dateTo.split('T')[0] : settlementDateStart;
      return settlementDateEnd < settlementDateStart
        ? { settlementDateStart: settlementDateEnd, settlementDateEnd: settlementDateStart }
        : { settlementDateStart, settlementDateEnd };
    }
    case 'equals':
    default:
      return { settlementDateStart, settlementDateEnd: settlementDateStart };
  }
}
